/**
 *
 * CreateMessage
 *
 */

import React, { useMemo } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from "react-i18next";

// styles
import './style.scss';

// layout components
import ErrorBoundary from 'engine/ErrorBoundary';

// antd component
import { Form, Input, Checkbox, Button, Typography, message } from 'antd';

// assets
import CustomIcons from 'components/CustomIcons';
import { CloseOutlined } from '@ant-design/icons';

// Communication components
import { AudioRecorder } from 'components/Audio';

// utils
import api from 'utils/api';
import { truncate } from 'utils/helpers/helper.general';
// hooks
import { useRecorder } from 'utils/hooks';
import _local from 'utils/localStorageUtils';


function CreateMessage({
  disabled,
  loading,
  onSend,
  setReply,
  reply,
  // default props
  className,
}) {
  const childClassNames = classNames('create-message-wrapper', className);
  const { t, i18n } = useTranslation();

  const localUser = _local.getLocalUser();

  const [discussionForm] = Form.useForm();

  const recorder = useRecorder();

  const messageContentValue = Form.useWatch(['content'], discussionForm);

  const handleFormFinish = values => {
    if (values?.content || recorder?.audio) {
      if (recorder?.audio) {
        const file = new File([recorder?.audio], `files-${uuidv4()}.wav`, {
          type: 'audio/wav',
        });

        const form = new FormData();
        form.append('audio', file);

        api.discussions.uploadVoiceMessage(form)
          .then(data => {
            onSend({
              // visibility: values.visibility ? 'admin' : 'all', // INFO: disable this feature for task SCHOOL-1101 
              visibility: "all",
              media: {
                type: 'audio',
                url: data.url,
              },
            });

            recorder?.deleteRecording();
          })
          .catch(err => {
            console.error(err);
            discussionForm.resetFields();
          });
      } else {
        onSend({
          // visibility: values.visibility ? 'admin' : 'all', // INFO: disable this feature for task SCHOOL-1101 
          visibility: "all",
          content: values.content,
        });
        discussionForm.resetFields();
      }
    } else {
      message.error(t("messages.mandatoryMessage"));
    }
  };

  const renderReplay = useMemo(() => { 
    return !!reply && (
      <div className="message-reply">
        <div className="message-reply--header">
          <CustomIcons
            type="reply-left"
            className="message-reply--header__icon"
          />
          <div className="message-reply--header__content">
            <span className="reply-name">
              {reply?.sender?.description?.firstname}{' '}
              {reply?.sender?.description?.lastname}
            </span>
            <div
              className="message-item__reply-text"
              dangerouslySetInnerHTML={{
                __html: reply?.content
                  ? truncate(reply?.content, 50)
                  : t("messages.voiceMessage")
              }}
            />
          </div>
        </div>
        <CloseOutlined onClick={() => setReply(null)} />
      </div>
    );
  }, [reply, setReply, i18n?.language]);

  return (
    <div className={childClassNames}>
      <ErrorBoundary>
        <div className="create-message-form">
          {renderReplay}
          <Form
            form={discussionForm}
            name="new_message_form"
            className="message-form"
            onFinish={handleFormFinish}
            disabled={disabled}
          >
            <div className="message-form--content">
              {!recorder?.isRecording && !recorder?.audio && (
                <Form.Item name="content" noStyle>
                  <Input.TextArea
                    rows={2}
                    placeholder={t("messages.yourMessage")}
                    className={classNames(
                      'message-form--content__message',
                      reply && 'with-reply',
                    )}
                  />
                </Form.Item>
              )}
              <AudioRecorder recorder={recorder} />
            </div>

            <div className="message-form--footer">
              {/* INFO: disable this feature for task SCHOOL-1101 */}
              {/*  {reply &&
                (localUser?.role === 'admin' || localUser?.role === 'teacher') &&
                reply.sender.role === 'parent' && (
                  <Form.Item name="visibility" valuePropName="checked" noStyle>
                    <Checkbox>
                      <span className="label">Rendre ce message uniquement visible par l’administration scolaire et les enseignants</span>
                    </Checkbox>
                  </Form.Item>
                )} */}

              <Form.Item noStyle>
                <Button
                  htmlType="submit"
                  className="message-form--footer__submit-btn"
                  type="primary"
                  loading={loading}
                  disabled={
                    !(messageContentValue?.length > 0 || recorder?.audio)
                  }
                >
                  {t("common.send")}
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>
      </ErrorBoundary>
    </div>
  );
}

CreateMessage.defaultProps = {
  loading: false,
  disabled: false,
};
CreateMessage.propTypes = {};

export default CreateMessage;
